import React, { useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Alert
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import { useDropzone } from 'react-dropzone'
import * as XLSX from 'xlsx'
import axios from 'axios'
import exportFromJSON from 'export-from-json'
import { expressURL } from '../../../services/api'

const ImportDeemedLogDialog = ({ open, onClose, onSuccess, plantId, token }) => {
    const baseUrl = expressURL || process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING || ''
    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const [result, setResult] = useState(null)

    const onDrop = (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            setFile(acceptedFiles[0])
            setError(null)
            setResult(null)
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls']
        },
        multiple: false
    })

    const handleDownloadTemplate = () => {
        // defined headers for template
        const data = [
            {
                'Plant Name': 'Example Plant',
                'Date': 'DD-MM-YYYY',
                'Start Time': '10:00',
                'End Time': '11:00',
                'Energy Loss': 100,
                'Fault Type': 'Grid Failure',
                'Description': 'Example description',
                'Accountability': 'DE',
                'Multiplying Factor': 1
            }
        ]
        const fileName = 'DeemedGenerationLog_Template'
        const exportType = exportFromJSON.types.xls
        exportFromJSON({ data, fileName, exportType })
    }

    const handleUpload = async () => {
        if (!file) return

        setUploading(true)
        setError(null)
        setResult(null)

        const formData = new FormData()
        formData.append('file', file)

        try {
            // Direct call or via service? using axios directly for file upload often easier to control
            const response = await axios.post(
                `${baseUrl}/deemed-generation/import`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            setResult(response.data)
            if (response.data.created > 0 || response.data.updated > 0) {
                setTimeout(() => {
                    onSuccess && onSuccess()
                    handleClose()
                }, 2000)
            }
        } catch (err) {
            console.error(err)
            setError(err.response?.data?.message || err.message || 'Upload failed')
        } finally {
            setUploading(false)
        }
    }

    const handleClose = () => {
        setFile(null)
        setError(null)
        setResult(null)
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Import Deemed Generation Log</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        size="small"
                        variant="outlined"
                    >
                        Download Template
                    </Button>
                </Box>

                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragActive ? '#f0f0f0' : '#fafafa',
                        '&:hover': {
                            borderColor: '#666'
                        }
                    }}
                >
                    <input {...getInputProps()} />
                    {file ? (
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {file.name}
                        </Typography>
                    ) : (
                        <>
                            <CloudUploadIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                            <Typography variant="body1">
                                Drag & drop an Excel file here, or click to select
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Accepted: .xlsx, .xls
                            </Typography>
                        </>
                    )}
                </Box>

                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                            Uploading and processing...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Import Completed: {result.created} created, {result.skipped} skipped.
                        {result.errors && result.errors.length > 0 && (
                            <Box component="ul" sx={{ mt: 1, pl: 2, maxHeight: '100px', overflowY: 'auto' }}>
                                {result.errors.map((e, i) => (
                                    <li key={i}>Row {e.row}: {e.message}</li>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!file || uploading}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ImportDeemedLogDialog
